/**
 * useDbWakeOnLogin Hook
 * Phase 7.16: DB Wake-on-Login Toast
 *
 * On the transition isAuthenticated: false → true, pings /health immediately.
 * Shows a Sonner toast if the database is paused/resuming.
 * Polls every 5 s until healthy or 60 s timeout.
 */

'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 5_000;
const MAX_WAIT_MS = 60_000;
const FETCH_TIMEOUT_MS = 10_000;
const TOAST_ID = 'db-wake';

const healthUrl = (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001/api/v1')
  .replace(/\/api\/v1\/?$/, '') + '/health';

type ErrorCategory = 'network_denied' | 'auth' | 'resuming' | 'timeout' | 'unknown';

interface HealthBody {
  status?: string;
  checks?: {
    database?: {
      status?: string;
      resuming?: boolean;
      errorCategory?: ErrorCategory;
    };
  };
}

async function pingHealth(): Promise<HealthBody | null> {
  try {
    const response = await fetch(healthUrl, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
    return await response.json() as HealthBody;
  } catch {
    return null;
  }
}

function isDbHealthy(body: HealthBody | null): boolean {
  if (!body) return false;
  return body.status === 'healthy' && body.checks?.database?.status === 'healthy';
}

function isDbDegraded(body: HealthBody | null): boolean {
  if (!body) return false;
  // Task 7.27.7: only treat as "resuming" when errorCategory confirms it is a cold-start,
  // not a network-denial or other non-retriable failure.
  const category = body.checks?.database?.errorCategory;
  if (category && category !== 'resuming') return false;
  return body.status === 'degraded' || body.checks?.database?.resuming === true;
}

function getErrorCategory(body: HealthBody | null): ErrorCategory {
  return body?.checks?.database?.errorCategory ?? 'unknown';
}

export function useDbWakeOnLogin(isAuthenticated: boolean): void {
  const wasAuthenticated = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only fire on the transition false → true
    if (!wasAuthenticated.current && isAuthenticated) {
      void checkAndWake();
    }
    wasAuthenticated.current = isAuthenticated;

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  async function checkAndWake(): Promise<void> {
    const body = await pingHealth();

    if (isDbHealthy(body)) {
      // Silent — no toast shown when healthy
      return;
    }

    if (isDbDegraded(body)) {
      toast.loading('Waking up the database, please wait…', { id: TOAST_ID });

      let elapsed = 0;

      intervalRef.current = setInterval(() => {
        elapsed += POLL_INTERVAL_MS;

        if (elapsed >= MAX_WAIT_MS) {
          // Timeout — replace loading toast with warning
          if (intervalRef.current) clearInterval(intervalRef.current);
          toast.warning('Database is taking longer than expected. Some features may be slow.', {
            id: TOAST_ID,
            duration: 8000,
          });
          return;
        }

        void (async () => {
          const latest = await pingHealth();
          if (isDbHealthy(latest)) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            toast.success('Database ready', { id: TOAST_ID, duration: 3000 });
          }
        })();
      }, POLL_INTERVAL_MS);
    } else {
      // Unhealthy / network failure — show category-appropriate message (Task 7.27.7)
      const category = getErrorCategory(body);
      if (category === 'network_denied') {
        toast.error('Database is currently unreachable. Please contact support if this persists.', { id: TOAST_ID });
      } else if (category === 'auth') {
        toast.error('Database authentication error. Please contact support.', { id: TOAST_ID });
      } else if (category === 'timeout') {
        toast.error('Database connection timed out. Please try again in a moment.', { id: TOAST_ID });
      } else {
        toast.error('Database unavailable. Please try again later.', { id: TOAST_ID });
      }
    }
  }
}
